import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  AlertCircle,
  GitCompare,
  Layers,
  Link2,
  RefreshCw,
} from "lucide-react";

import { ContextBar, MarketplaceCard } from "@/components/catalog";
import { DashboardLayout } from "@/components/layout";
import {
  Button,
  Card,
  CardContent,
  ConfirmationModal,
  PageHeader,
  showToast,
  StatusBadge,
} from "@/components/ui";
import { useCatalog } from "@/contexts/CatalogContext";

export default function CatalogOverview() {
  const navigate = useNavigate();
  const { currentMarketplaces } = useCatalog();
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncReason, setSyncReason] = useState("");
  const [selectedForSync, setSelectedForSync] = useState<any>(null);

  const connectedCount = currentMarketplaces.filter(
    (m: any) => m.status === "ACTIVE",
  ).length;

  const pendingReview = 45;

  const overallSyncStatus = currentMarketplaces.some(
    (m: any) => m.syncStatus === "FAILED",
  )
    ? "FAILED"
    : currentMarketplaces.some((m: any) => m.syncStatus === "SYNCING")
      ? "SYNCING"
      : "SYNCED";

  const handleStartSync = (marketplace: any) => {
    setSelectedForSync(marketplace);
    setShowSyncModal(true);
  };

  const handleConfirmSync = () => {
    showToast.success(
      "Sync started",
      `Syncing ${selectedForSync?.name} against Canonical model...`,
    );
    setShowSyncModal(false);
    setSyncReason("");
    setSelectedForSync(null);
  };

  const handleGoToSync = (marketplace: any) => {
    navigate(`/catalog/sync/${String(marketplace.code).toLowerCase()}`);
  };

  return (
    <DashboardLayout activePath="/catalog">
      <PageHeader
        title="Catalog Overview"
        subtitle="Manage catalog synchronization across marketplaces via the Canonical model"
        breadcrumbs={[
          { label: "Catalog", active: false },
          { label: "Overview", active: true },
        ]}
        actions={
          <>
            <Button
              data-testid="compare-btn"
              variant="outline"
              onClick={() => navigate("/catalog/compare")}
            >
              <GitCompare className="w-4 h-4" />
              Compare Marketplaces
            </Button>
            <Button
              data-testid="start-sync-btn"
              onClick={() => {
                const first = currentMarketplaces.find(
                  (m) => m.status === "ACTIVE",
                );
                navigate(
                  first
                    ? `/catalog/sync/${first.code.toLowerCase()}`
                    : "/catalog/sync",
                );
              }}
            >
              <RefreshCw className="w-4 h-4" />
              Start Marketplace Sync
            </Button>
          </>
        }
      />

      <ContextBar />

      <div
        data-testid="kpi-grid"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
      >
        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
              <Layers className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Canonical Model
              </p>
              <p className="text-xl font-bold font-heading text-slate-900 dark:text-slate-100">
                SmartStore
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400">
              <Link2 className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Connected Marketplaces
              </p>
              <p className="text-xl font-bold font-heading text-slate-900 dark:text-slate-100">
                {connectedCount} / {currentMarketplaces.length}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400">
              <RefreshCw className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Last Sync Status
              </p>
              <StatusBadge
                status={
                  overallSyncStatus === "SYNCED"
                    ? "ACTIVE"
                    : overallSyncStatus === "SYNCING"
                      ? "PENDING"
                      : "FAILED"
                }
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400">
              <AlertCircle className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Items Pending Review
              </p>
              <p className="text-xl font-bold font-heading text-slate-900 dark:text-slate-100">
                {pendingReview}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
          Marketplaces
        </h2>
        <div
          data-testid="marketplace-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {currentMarketplaces.map((marketplace: any) => (
            <MarketplaceCard
              key={marketplace.id}
              marketplace={marketplace}
              onSync={handleStartSync}
              onGoToSync={handleGoToSync}
            />
          ))}
        </div>
      </div>

      <ConfirmationModal
        open={showSyncModal}
        onOpenChange={setShowSyncModal}
        title={`Sync ${selectedForSync?.name} Catalog`}
        description={`This will sync the ${selectedForSync?.name} catalog against the SmartStore Canonical model. New Canonical mappings will be auto-suggested.`}
        confirmLabel="Start Sync"
        variant="primary"
        requireReason
        reason={syncReason}
        onReasonChange={setSyncReason}
        onConfirm={handleConfirmSync}
        onCancel={() => setShowSyncModal(false)}
      />
    </DashboardLayout>
  );
}
